import { Outlet, Navigate, NavLink, useLocation } from "react-router-dom";
import { ProgressProvider } from "@/components/ui/ProgressContext.tsx";
import { useAuthStore } from "@/stores/authStore.ts";
import { navLit } from "@/lib/admin-nav.ts";   // (tf3-43)
import {
  BarChart3, Users, CreditCard, Settings, ScrollText, ArrowLeft, Shield,
  Layers, Building2, Key, MessageSquare, Tag, Share2, Trophy, Target, Boxes, Video, Type, UserRound, Sparkles, FlaskConical, Scissors, GitBranch, ClipboardCheck, UserSearch, TrendingUp, ListChecks, Activity, Languages } from "lucide-react";

const ADMIN_NAV = [
  { to: "/admin",             icon: BarChart3,     label: "Dashboard", end: true },
  { to: "/admin/operations",  icon: Activity,      label: "Operations" },
  { to: "/admin/words",       icon: Languages,     label: "Words" },   // (B107) the customer's words
  { to: "/admin/waiting",     icon: ListChecks,    label: "Waiting on you" },   // (TF-6b-4)
  // the Run Inspector (filmstrip, verdicts, delivered video) — it had a route but no menu entry
  { to: "/admin/runs",        icon: Layers,        label: "Runs" },
  { to: "/admin/engine-state", icon: Layers,       label: "Engine state" },
  { to: "/admin/videos",      icon: Video,         label: "Videos"              },
  { to: "/admin/research",    icon: FlaskConical,  label: "Research / Arcs"     },
  { to: "/admin/research-arcs", icon: GitBranch,     label: "Research Arcs"       },
  { to: "/admin/runs",        icon: ClipboardCheck, label: "Finishing"          },
  // (X11 / X10's first step, 2026-10-05) the ENGINE console — its own sign-in; covers is its first piece
  { to: "/admin/engine-console", icon: Shield,     label: "Engine console"      },
  { to: "/admin/teardowns",   icon: Scissors,      label: "Covers (engine)"     },
  { to: "/admin/users",       icon: Users,         label: "Users"               },
  { to: "/admin/gateways",    icon: CreditCard,    label: "Gateways"            },
  { to: "/admin/providers",   icon: Boxes,         label: "Providers"           },
  { to: "/admin/presets",     icon: Layers,        label: "Presets"             },
  { to: "/admin/video-types", icon: Layers,        label: "Video types"         },   // session 7 3.8
  { to: "/admin/caption-styles", icon: Type,     label: "Caption Styles"      },
  { to: "/admin/stock-avatars", icon: UserRound,  label: "Stock Avatars"       },
  { to: "/admin/characters",  icon: Sparkles,      label: "Characters"          },
  { to: "/admin/casting",     icon: UserSearch,    label: "Casting"             },
  { to: "/admin/growth",      icon: TrendingUp,    label: "Audience Growth"     },
  { to: "/admin/growth/queue", icon: ListChecks,   label: "Review Queue"        },
  { to: "/admin/series",      icon: GitBranch,     label: "Series"              },   // (TF-6b-2)
  { to: "/admin/slates",      icon: Sparkles,      label: "Slates", notUnder: "/admin/slates/learning" },   // (TF-6b-3) (tf3-43) not lit on Slate learning
  { to: "/admin/slates/learning", icon: TrendingUp, label: "Slate learning"   },   // (tf3-42)
  { to: "/admin/audiences",   icon: Users,         label: "Audiences"           },   // (tf3-42)
  { to: "/admin/cast",        icon: UserSearch,    label: "Cast candidates"     },   // (B101)
  { to: "/admin/tenants",     icon: Building2,     label: "Tenants"             },
  { to: "/admin/api-keys",    icon: Key,           label: "API Keys"            },
  { to: "/admin/feedback",    icon: MessageSquare, label: "Feedback"            },
  { to: "/admin/promos",      icon: Tag,           label: "Promos"              },
  { to: "/admin/referrals",   icon: Share2,        label: "Referrals"           },
  { to: "/admin/showcase",    icon: Trophy,        label: "Showcase"            },
  { to: "/admin/attribution", icon: Target,        label: "Attribution"         },
  { to: "/admin/settings",    icon: Settings,      label: "Settings"            },
  { to: "/admin/audit",       icon: ScrollText,    label: "Audit Log"           },
];

export function AdminLayout() {
  const user = useAuthStore((s) => s.user);
  const { pathname } = useLocation();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="flex h-screen bg-surface-900 overflow-hidden">
      {/* Admin sidebar */}
      <aside className="hidden md:flex w-[200px] flex-col bg-surface-900 border-r border-surface-800 flex-shrink-0">
        <div className="px-4 py-4 border-b border-surface-800 flex items-center gap-2.5">
          <Shield className="w-4 h-4 text-red-400 flex-shrink-0" />
          <span className="text-white font-semibold text-[14px]">Admin Panel</span>
        </div>
        <nav className="flex-1 px-2.5 py-3 space-y-0.5 overflow-y-auto">
          {ADMIN_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg text-[12.5px] font-medium min-h-[36px] transition-colors ${
                  navLit(isActive, pathname, item.notUnder)
                    ? "bg-red-600/15 text-red-400"
                    : "text-surface-400 hover:text-surface-200 hover:bg-surface-800"
                }`
              }
            >
              <item.icon className="w-[15px] h-[15px] flex-shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-2.5 py-3 border-t border-surface-800">
          <NavLink
            to="/dashboard"
            className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg text-[12.5px] text-surface-500 hover:text-surface-300 hover:bg-surface-800 transition-colors min-h-[36px]"
          >
            <ArrowLeft className="w-[15px] h-[15px] flex-shrink-0" />
            Back to App
          </NavLink>
        </div>
      </aside>

      {/* Right panel */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Admin top bar */}
        <header className="flex items-center gap-2 px-5 md:px-6 py-3.5 bg-white border-b border-surface-200 flex-shrink-0 min-h-[56px]">
          <Shield className="w-4 h-4 text-red-500 flex-shrink-0 md:hidden" />
          {/* Mobile horizontal scroll nav */}
          <div className="flex items-center gap-1 overflow-x-auto md:hidden">
            {ADMIN_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                    navLit(isActive, pathname, item.notUnder) ? "bg-red-50 text-red-600" : "text-surface-500"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
          <span className="hidden md:block text-[15px] font-semibold text-surface-900">Admin</span>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-surface-50">
          <div className="px-4 py-5 md:px-6 md:py-6 max-w-[1200px]">
            <ProgressProvider><Outlet /></ProgressProvider>
          </div>
        </main>
      </div>
    </div>
  );
}
